import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Modal, Button, Form, Badge, Tabs, Tab } from "react-bootstrap";
import { FiPlus, FiTrash2, FiEdit2, FiSearch } from "react-icons/fi";
import ConfirmModal from "../components/ConfirmModal";
import api from "../services/api";

const Inventory = () => {
  const [activeTab, setActiveTab] = useState("products");
  // Products
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: "",
    category_id: "",
    sku: "",
    description: "",
    buy_price: "",
    sell_price: "",
    stock: "",
    min_stock: "5",
    unit: "pcs",
  });
  const [filterCat, setFilterCat] = useState("");
  const [search, setSearch] = useState("");

  // Stock
  const [movements, setMovements] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [adjustForm, setAdjustForm] = useState({
    quantity: "",
    type: "in",
    notes: "",
  });
  const [viewMode, setViewMode] = useState("all");

  // Purchases
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({
    supplier_id: "",
    notes: "",
    items: [],
  });
  const [showDetail, setShowDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  // Category CRUD
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
  });
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const openConfirm = (title, message, onConfirm) =>
    setConfirmModal({ show: true, title, message, onConfirm });
  const closeConfirm = () =>
    setConfirmModal((prev) => ({ ...prev, show: false, onConfirm: null }));

  useEffect(() => {
    loadData();
  }, []);
  useEffect(() => {
    if (activeTab === "stock") loadProducts();
  }, [viewMode]);

  const loadData = async () => {
    try {
      const [prodRes, catRes, purRes, supRes] = await Promise.all([
        api.get("/products"),
        api.get("/products/categories/list"),
        api.get("/purchases"),
        api.get("/purchases/suppliers/list"),
      ]);
      setProducts(prodRes.data);
      setCategories(catRes.data);
      setPurchases(purRes.data);
      setSuppliers(supRes.data);
    } catch (error) {
      toast.error("Gagal memuat data");
    }
  };

  const loadProducts = async () => {
    try {
      const params = viewMode === "low" ? { low_stock: "1" } : {};
      if (filterCat) params.category_id = filterCat;
      if (search) params.search = search;
      const res = await api.get("/products", { params });
      setProducts(res.data);
    } catch (error) {
      toast.error("Gagal memuat data");
    }
  };

  const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
  const fmtDt = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    return (
      dt.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) +
      `, ${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`
    );
  };

  // ========== Products ==========
  const handleProductSubmit = async () => {
    if (!productForm.name || !productForm.sell_price) {
      toast.error("Nama dan harga jual wajib diisi");
      return;
    }
    try {
      if (editProduct) {
        await api.put(`/products/${editProduct.id}`, {
          ...productForm,
          is_active: 1,
        });
        toast.success("Produk berhasil diupdate");
      } else {
        await api.post("/products", productForm);
        toast.success("Produk berhasil ditambahkan");
      }
      setShowProductModal(false);
      resetProductForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal menyimpan");
    }
  };

  const handleEditProduct = (prod) => {
    setEditProduct(prod);
    setProductForm({
      name: prod.name,
      category_id: prod.category_id || "",
      sku: prod.sku || "",
      description: prod.description || "",
      buy_price: prod.buy_price,
      sell_price: prod.sell_price,
      stock: prod.stock,
      min_stock: prod.min_stock,
      unit: prod.unit,
    });
    setShowProductModal(true);
  };

  const resetProductForm = () => {
    setEditProduct(null);
    setProductForm({
      name: "",
      category_id: "",
      sku: "",
      description: "",
      buy_price: "",
      sell_price: "",
      stock: "",
      min_stock: "5",
      unit: "pcs",
    });
  };

  // ========== Stock ==========
  const handleAdjust = async () => {
    if (!adjustForm.quantity || parseInt(adjustForm.quantity) <= 0) {
      toast.error("Quantity harus lebih dari 0");
      return;
    }
    try {
      await api.post(
        `/products/${selectedProduct.id}/adjust-stock`,
        adjustForm,
      );
      toast.success("Stok berhasil diupdate");
      setShowAdjustModal(false);
      setAdjustForm({ quantity: "", type: "in", notes: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal update stok");
    }
  };

  const openHistory = async (product) => {
    setSelectedProduct(product);
    try {
      const res = await api.get(`/products/${product.id}/stock-movements`);
      setMovements(res.data);
    } catch (error) {
      toast.error("Gagal memuat riwayat stok");
    }
    setShowHistoryModal(true);
  };

  // ========== Category CRUD ==========
  const handleCategorySubmit = async () => {
    if (!categoryForm.name) {
      toast.error("Nama kategori wajib diisi");
      return;
    }
    try {
      if (editCategory) {
        await api.put(`/products/categories/${editCategory.id}`, categoryForm);
        toast.success("Kategori berhasil diupdate");
      } else {
        await api.post("/products/categories", categoryForm);
        toast.success("Kategori berhasil ditambahkan");
      }
      setShowCategoryModal(false);
      setEditCategory(null);
      setCategoryForm({ name: "", description: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal menyimpan kategori");
    }
  };

  const handleEditCategory = (cat) => {
    setEditCategory(cat);
    setCategoryForm({ name: cat.name, description: cat.description || "" });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = (cat) => {
    openConfirm(
      "Hapus Kategori",
      `Hapus kategori "${cat.name}"? Kategori tidak bisa dihapus jika masih memiliki produk.`,
      async () => {
        closeConfirm();
        try {
          await api.delete(`/products/categories/${cat.id}`);
          toast.success("Kategori berhasil dihapus");
          loadData();
        } catch (error) {
          toast.error(
            error.response?.data?.message || "Gagal menghapus kategori",
          );
        }
      },
    );
  };

  // ========== Purchases ==========
  const addPurchaseItem = () => {
    setPurchaseForm({
      ...purchaseForm,
      items: [
        ...purchaseForm.items,
        { product_id: "", quantity: 1, buy_price: 0 },
      ],
    });
  };

  const removePurchaseItem = (index) => {
    setPurchaseForm({
      ...purchaseForm,
      items: purchaseForm.items.filter((_, i) => i !== index),
    });
  };

  const updatePurchaseItem = (index, field, value) => {
    const newItems = [...purchaseForm.items];
    newItems[index][field] = value;
    if (field === "product_id") {
      const prod = products.find((p) => p.id === parseInt(value));
      if (prod) newItems[index].buy_price = prod.buy_price;
    }
    setPurchaseForm({ ...purchaseForm, items: newItems });
  };

  const handlePurchaseSubmit = async () => {
    if (purchaseForm.items.length === 0) {
      toast.error("Tambahkan minimal 1 item");
      return;
    }
    setLoading(true);
    try {
      await api.post("/purchases", purchaseForm);
      toast.success("Pembelian berhasil dicatat");
      setShowPurchaseModal(false);
      setPurchaseForm({ supplier_id: "", notes: "", items: [] });
      loadData();
    } catch (error) {
      toast.error("Gagal menyimpan pembelian");
    } finally {
      setLoading(false);
    }
  };

  const viewPurchaseDetail = async (id) => {
    try {
      const res = await api.get(`/purchases/${id}`);
      setShowDetail(res.data);
    } catch (error) {
      toast.error("Gagal memuat detail");
    }
  };

  const totalPurchase = purchaseForm.items.reduce(
    (sum, i) => sum + i.buy_price * i.quantity,
    0,
  );
  const stockProducts =
    viewMode === "low"
      ? products.filter((p) => p.stock <= p.min_stock)
      : products;

  return (
    <div>
      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-4">
        {/* ===== TAB: PRODUK ===== */}
        <Tab eventKey="products" title="Produk">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex gap-2 flex-grow-1" style={{ maxWidth: 500 }}>
              <div className="position-relative flex-grow-1">
                <FiSearch
                  className="position-absolute"
                  style={{ left: 12, top: 11, color: "#94a3b8" }}
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Cari produk..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ paddingLeft: 36 }}
                />
              </div>
              <select
                className="form-select"
                value={filterCat}
                onChange={(e) => setFilterCat(e.target.value)}
                style={{ width: "auto" }}
              >
                <option value="">Semua Kategori</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <button className="btn btn-primary" onClick={loadProducts}>
                Filter
              </button>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => {
                resetProductForm();
                setShowProductModal(true);
              }}
            >
              <FiPlus className="me-1" /> Tambah Produk
            </button>
          </div>

          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Nama</th>
                    <th>Kategori</th>
                    <th className="text-end">Harga Modal</th>
                    <th className="text-end">Harga Jual</th>
                    <th className="text-center">Stok</th>
                    <th>Satuan</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr
                      key={p.id}
                      className={p.stock <= p.min_stock ? "low-stock" : ""}
                    >
                      <td>
                        <code>{p.sku}</code>
                      </td>
                      <td className="fw-bold">{p.name}</td>
                      <td>{p.category_name || "-"}</td>
                      <td className="text-end">{fmt(p.buy_price)}</td>
                      <td className="text-end">{fmt(p.sell_price)}</td>
                      <td className="text-center">
                        <Badge
                          bg={p.stock <= p.min_stock ? "danger" : "success"}
                        >
                          {p.stock}
                        </Badge>
                      </td>
                      <td>{p.unit}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleEditProduct(p)}
                        >
                          <FiEdit2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {products.length === 0 && (
                    <tr>
                      <td colSpan="8" className="text-center text-muted py-4">
                        Belum ada produk
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Tab>

        {/* ===== TAB: STOK ===== */}
        <Tab eventKey="stock" title="Stok">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="btn-group">
              <button
                className={`btn btn-sm ${viewMode === "all" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setViewMode("all")}
              >
                Semua
              </button>
              <button
                className={`btn btn-sm ${viewMode === "low" ? "btn-danger" : "btn-outline-danger"}`}
                onClick={() => setViewMode("low")}
              >
                Stok Menipis
              </button>
            </div>
          </div>

          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Produk</th>
                    <th>Kategori</th>
                    <th className="text-center">Stok</th>
                    <th className="text-center">Min. Stok</th>
                    <th>Satuan</th>
                    <th className="text-end">Nilai Stok</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {stockProducts.map((p) => (
                    <tr
                      key={p.id}
                      className={p.stock <= p.min_stock ? "low-stock" : ""}
                    >
                      <td className="fw-bold">
                        {p.name} <small className="text-muted">({p.sku})</small>
                      </td>
                      <td>{p.category_name}</td>
                      <td className="text-center">
                        <Badge
                          bg={
                            p.stock <= p.min_stock
                              ? "danger"
                              : p.stock <= p.min_stock * 2
                                ? "warning"
                                : "success"
                          }
                        >
                          {p.stock}
                        </Badge>
                      </td>
                      <td className="text-center">{p.min_stock}</td>
                      <td>{p.unit}</td>
                      <td className="text-end">{fmt(p.stock * p.buy_price)}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              setSelectedProduct(p);
                              setShowAdjustModal(true);
                            }}
                          >
                            Adjust
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => openHistory(p)}
                          >
                            Riwayat
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Tab>

        {/* ===== TAB: PEMBELIAN ===== */}
        <Tab eventKey="purchases" title="Pembelian">
          <div className="d-flex justify-content-end mb-3">
            <button
              className="btn btn-primary"
              onClick={() => setShowPurchaseModal(true)}
            >
              <FiPlus className="me-1" /> Pembelian Baru
            </button>
          </div>

          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Supplier</th>
                    <th className="text-end">Total</th>
                    <th>Tanggal</th>
                    <th>Oleh</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <code>{p.invoice_number}</code>
                      </td>
                      <td>{p.supplier_name || "-"}</td>
                      <td className="text-end fw-bold">
                        {fmt(p.total_amount)}
                      </td>
                      <td>{fmtDt(p.created_at)}</td>
                      <td>{p.created_by_name}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => viewPurchaseDetail(p.id)}
                        >
                          Detail
                        </button>
                      </td>
                    </tr>
                  ))}
                  {purchases.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        Belum ada data
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Tab>

        {/* ===== TAB: KATEGORI ===== */}
        <Tab eventKey="categories" title="Kategori">
          <div className="d-flex justify-content-end mb-3">
            <button
              className="btn btn-primary"
              onClick={() => {
                setEditCategory(null);
                setCategoryForm({ name: "", description: "" });
                setShowCategoryModal(true);
              }}
            >
              <FiPlus className="me-1" /> Tambah Kategori
            </button>
          </div>
          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Nama Kategori</th>
                    <th>Deskripsi</th>
                    <th>Jumlah Produk</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat) => {
                    const prodCount = products.filter(
                      (p) => p.category_id === cat.id,
                    ).length;
                    return (
                      <tr key={cat.id}>
                        <td className="fw-bold">{cat.name}</td>
                        <td className="text-muted">{cat.description || "-"}</td>
                        <td>
                          <Badge bg={prodCount > 0 ? "primary" : "secondary"}>
                            {prodCount} produk
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEditCategory(cat)}
                            >
                              <FiEdit2 size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteCategory(cat)}
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {categories.length === 0 && (
                    <tr>
                      <td colSpan="4" className="text-center text-muted py-4">
                        Belum ada kategori
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Tab>
      </Tabs>

      {/* ===== MODALS ===== */}

      {/* Product Modal */}
      <Modal
        show={showProductModal}
        onHide={() => setShowProductModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editProduct ? "Edit Produk" : "Tambah Produk"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-2">
            <div className="col-8">
              <Form.Group className="mb-2">
                <Form.Label>Nama Produk *</Form.Label>
                <Form.Control
                  value={productForm.name}
                  onChange={(e) =>
                    setProductForm({ ...productForm, name: e.target.value })
                  }
                />
              </Form.Group>
            </div>
            <div className="col-4">
              <Form.Group className="mb-2">
                <Form.Label>SKU</Form.Label>
                <Form.Control
                  value={productForm.sku}
                  onChange={(e) =>
                    setProductForm({ ...productForm, sku: e.target.value })
                  }
                />
              </Form.Group>
            </div>
            <div className="col-6">
              <Form.Group className="mb-2">
                <Form.Label>Kategori</Form.Label>
                <Form.Select
                  value={productForm.category_id}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      category_id: e.target.value,
                    })
                  }
                >
                  <option value="">Pilih Kategori</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-6">
              <Form.Group className="mb-2">
                <Form.Label>Satuan</Form.Label>
                <Form.Select
                  value={productForm.unit}
                  onChange={(e) =>
                    setProductForm({ ...productForm, unit: e.target.value })
                  }
                >
                  <option value="pcs">pcs</option>
                  <option value="botol">botol</option>
                  <option value="kaleng">kaleng</option>
                  <option value="pack">pack</option>
                  <option value="box">box</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-6">
              <Form.Group className="mb-2">
                <Form.Label>Harga Modal</Form.Label>
                <Form.Control
                  type="number"
                  value={productForm.buy_price}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      buy_price: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </div>
            <div className="col-6">
              <Form.Group className="mb-2">
                <Form.Label>Harga Jual *</Form.Label>
                <Form.Control
                  type="number"
                  value={productForm.sell_price}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      sell_price: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </div>
            <div className="col-4">
              <Form.Group className="mb-2">
                <Form.Label>Stok</Form.Label>
                <Form.Control
                  type="number"
                  value={productForm.stock}
                  onChange={(e) =>
                    setProductForm({ ...productForm, stock: e.target.value })
                  }
                  disabled={!!editProduct}
                />
              </Form.Group>
            </div>
            <div className="col-4">
              <Form.Group className="mb-2">
                <Form.Label>Min. Stok</Form.Label>
                <Form.Control
                  type="number"
                  value={productForm.min_stock}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      min_stock: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowProductModal(false)}
          >
            Batal
          </Button>
          <Button variant="primary" onClick={handleProductSubmit}>
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Adjust Stock Modal */}
      <Modal
        show={showAdjustModal}
        onHide={() => setShowAdjustModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Penyesuaian Stok - {selectedProduct?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Stok saat ini:{" "}
            <strong>
              {selectedProduct?.stock} {selectedProduct?.unit}
            </strong>
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Tipe</Form.Label>
            <Form.Select
              value={adjustForm.type}
              onChange={(e) =>
                setAdjustForm({ ...adjustForm, type: e.target.value })
              }
            >
              <option value="in">Stok Masuk (+)</option>
              <option value="out">Stok Keluar (-)</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Quantity</Form.Label>
            <Form.Control
              type="number"
              value={adjustForm.quantity}
              onChange={(e) =>
                setAdjustForm({ ...adjustForm, quantity: e.target.value })
              }
              min="1"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Catatan</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={adjustForm.notes}
              onChange={(e) =>
                setAdjustForm({ ...adjustForm, notes: e.target.value })
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdjustModal(false)}>
            Batal
          </Button>
          <Button variant="primary" onClick={handleAdjust}>
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Stock History Modal */}
      <Modal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Riwayat Stok - {selectedProduct?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Tanggal</th>
                <th>Tipe</th>
                <th className="text-center">Qty</th>
                <th>Referensi</th>
                <th>Catatan</th>
                <th>Oleh</th>
              </tr>
            </thead>
            <tbody>
              {movements.map((m) => (
                <tr key={m.id}>
                  <td>{fmtDt(m.created_at)}</td>
                  <td>
                    <Badge
                      bg={
                        m.type === "in"
                          ? "success"
                          : m.type === "out"
                            ? "danger"
                            : "warning"
                      }
                    >
                      {m.type === "in"
                        ? "Masuk"
                        : m.type === "out"
                          ? "Keluar"
                          : "Adjust"}
                    </Badge>
                  </td>
                  <td className="text-center">
                    {m.type === "in" ? "+" : "-"}
                    {m.quantity}
                  </td>
                  <td>{m.reference_type}</td>
                  <td>{m.notes}</td>
                  <td>{m.created_by_name}</td>
                </tr>
              ))}
              {movements.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center text-muted">
                    Belum ada riwayat
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Modal.Body>
      </Modal>

      {/* New Purchase Modal */}
      <Modal
        show={showPurchaseModal}
        onHide={() => setShowPurchaseModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Pembelian Baru</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Supplier</Form.Label>
                <Form.Select
                  value={purchaseForm.supplier_id}
                  onChange={(e) =>
                    setPurchaseForm({
                      ...purchaseForm,
                      supplier_id: e.target.value,
                    })
                  }
                >
                  <option value="">-- Pilih Supplier --</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Catatan</Form.Label>
                <Form.Control
                  value={purchaseForm.notes}
                  onChange={(e) =>
                    setPurchaseForm({ ...purchaseForm, notes: e.target.value })
                  }
                />
              </Form.Group>
            </div>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <h6 className="mb-0">Item Pembelian</h6>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={addPurchaseItem}
            >
              <FiPlus /> Tambah Item
            </button>
          </div>
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Produk</th>
                <th>Qty</th>
                <th>Harga Beli</th>
                <th>Subtotal</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {purchaseForm.items.map((item, i) => (
                <tr key={i}>
                  <td>
                    <Form.Select
                      size="sm"
                      value={item.product_id}
                      onChange={(e) =>
                        updatePurchaseItem(i, "product_id", e.target.value)
                      }
                    >
                      <option value="">Pilih produk</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.sku})
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) =>
                        updatePurchaseItem(
                          i,
                          "quantity",
                          parseInt(e.target.value),
                        )
                      }
                      style={{ width: 80 }}
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      value={item.buy_price}
                      onChange={(e) =>
                        updatePurchaseItem(
                          i,
                          "buy_price",
                          parseFloat(e.target.value),
                        )
                      }
                      style={{ width: 120 }}
                    />
                  </td>
                  <td className="fw-bold">
                    {fmt(item.buy_price * item.quantity)}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm text-danger"
                      onClick={() => removePurchaseItem(i)}
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-end fw-bold">Total: {fmt(totalPurchase)}</div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPurchaseModal(false)}
          >
            Batal
          </Button>
          <Button
            variant="primary"
            onClick={handlePurchaseSubmit}
            disabled={loading}
          >
            {loading ? "Menyimpan..." : "Simpan Pembelian"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Purchase Detail Modal */}
      <Modal show={!!showDetail} onHide={() => setShowDetail(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Detail Pembelian</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showDetail && (
            <>
              <p>
                <strong>Invoice:</strong> {showDetail.invoice_number}
              </p>
              <p>
                <strong>Supplier:</strong> {showDetail.supplier_name || "-"}
              </p>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Produk</th>
                    <th className="text-end">Qty</th>
                    <th className="text-end">Harga</th>
                    <th className="text-end">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {showDetail.details?.map((d) => (
                    <tr key={d.id}>
                      <td>{d.product_name}</td>
                      <td className="text-end">{d.quantity}</td>
                      <td className="text-end">{fmt(d.buy_price)}</td>
                      <td className="text-end">{fmt(d.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-end fw-bold">
                Total: {fmt(showDetail.total_amount)}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Category Modal */}
      <Modal
        show={showCategoryModal}
        onHide={() => {
          setShowCategoryModal(false);
          setEditCategory(null);
        }}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editCategory ? "Edit Kategori" : "Tambah Kategori"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nama Kategori *</Form.Label>
            <Form.Control
              value={categoryForm.name}
              onChange={(e) =>
                setCategoryForm({ ...categoryForm, name: e.target.value })
              }
              placeholder="Contoh: Minuman"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Deskripsi</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={categoryForm.description}
              onChange={(e) =>
                setCategoryForm({
                  ...categoryForm,
                  description: e.target.value,
                })
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowCategoryModal(false);
              setEditCategory(null);
            }}
          >
            Batal
          </Button>
          <Button variant="primary" onClick={handleCategorySubmit}>
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default Inventory;
